import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../api/config';
import toast from 'react-hot-toast';
import { Search, X, ShoppingCart, UtensilsCrossed, Tag, Package, RefreshCw, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react';

export default function Menu() {
  const { vendor } = useAuth();
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [toggling,  setToggling]  = useState(null); // item id being toggled
  const [search,    setSearch]    = useState('');
  const [filterCat, setFilterCat] = useState('all');

  const vd        = vendor?.vendor || vendor;
  const isGrocery = vd?.businessType === 'grocery';

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const id = vd?.restaurantId || vd?.storeId;
      if (!id) { toast.error('Store ID not found!'); setLoading(false); return; }

      const endpoint = isGrocery
        ? `/grocery/stores/${id}/items`
        : `/menu/restaurant/${id}`;

      const res = await API.get(endpoint);
      setItems(res.data?.data || res.data?.items || res.data || []);
    } catch { toast.error('Failed to load menu. Please try again.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (vd) fetchMenu(); }, []);

  // ── Toggle item availability (vendor permission only) ──
  const toggleAvailability = async (item) => {
    const itemId  = item._id;
    const newVal  = !(item.isAvailable !== false && item.isActive !== false);
    setToggling(itemId);
    try {
      // Try grocery endpoint first, fall back to menu endpoint
      const endpoint = isGrocery
        ? `/grocery/items/${itemId}`
        : `/menu/${itemId}`;

      await API.patch(endpoint, {
        isAvailable: newVal,
        isActive:    newVal,
      });

      // Update local state immediately for snappy UX
      setItems(prev => prev.map(i =>
        i._id === itemId
          ? { ...i, isAvailable: newVal, isActive: newVal }
          : i
      ));

      toast.success(
        newVal
          ? `"${item.name}" is now available.`
          : `"${item.name}" marked as unavailable.`
      );
    } catch {
      toast.error('Failed to update item. Please try again.');
    }
    setToggling(null);
  };

  const categories = ['all', ...new Set(items.map(i => i.category).filter(Boolean))];
  const filtered   = items.filter(item => {
    const matchSearch = item.name?.toLowerCase().includes(search.toLowerCase());
    const matchCat    = filterCat === 'all' || item.category === filterCat;
    return matchSearch && matchCat;
  });

  const availableCount   = items.filter(i => i.isAvailable !== false && i.isActive !== false).length;
  const unavailableCount = items.length - availableCount;

  return (
    <div style={S.page}>

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>{isGrocery ? 'Products' : 'Menu'}</div>
          <div style={S.sub}>{items.length} items total</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:'#555' }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:'#10b981', display:'inline-block' }}/>
            {availableCount} available
          </span>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:'#555' }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:'#ef4444', display:'inline-block' }}/>
            {unavailableCount} off
          </span>
          <button style={S.refreshBtn} onClick={fetchMenu}>
            <RefreshCw size={16} color='#666' style={{ animation: loading ? 'spin 1s linear infinite':'none' }}/>
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div style={{
        background:'#eff6ff', border:'1px solid #bfdbfe',
        borderRadius:12, padding:'10px 14px', marginBottom:14,
        display:'flex', alignItems:'center', gap:8,
        fontSize:12, color:'#2563eb',
      }}>
        <Package size={14} color='#2563eb'/>
        <span>
          <strong>Toggle availability</strong> to mark items as in stock or out of stock.
          Contact your admin to add or edit items.
        </span>
      </div>

      {/* Search */}
      <div style={S.search}>
        <Search size={16} color='#bbb'/>
        <input
          style={S.searchInput}
          placeholder={`Search ${isGrocery ? 'products' : 'menu items'}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button onClick={() => setSearch('')}><X size={15} color='#bbb'/></button>}
      </div>

      {/* Category filter */}
      {categories.length > 1 && (
        <div style={S.cats}>
          {categories.map(cat => (
            <button
              key={cat}
              style={{ ...S.cat, ...(filterCat===cat ? S.catActive : {}) }}
              onClick={() => setFilterCat(cat)}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={S.empty}>
          <Loader2 size={28} color='#ff6b35' style={{ animation:'spin 1s linear infinite' }}/>
          <p style={{ marginTop:12, color:'#888' }}>Loading {isGrocery ? 'products' : 'menu'}...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={S.empty}>
          <UtensilsCrossed size={48} color='#e0e0e0'/>
          <p style={{ marginTop:14, color:'#888' }}>No items found</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map((item, i) => {
            const isAvail    = item.isAvailable !== false && item.isActive !== false;
            const isToggling = toggling === item._id;

            return (
              <div key={i} style={{
                background:'#fff', borderRadius:14,
                border: `1.5px solid ${isAvail ? '#e5e7eb' : '#fecaca'}`,
                padding:'14px 16px',
                display:'flex', alignItems:'center', gap:14,
                boxShadow:'0 2px 8px rgba(0,0,0,0.04)',
                opacity: isAvail ? 1 : 0.75,
                transition:'all 0.2s',
              }}>

                {/* Image or placeholder */}
                {item.image ? (
                  <img src={item.image} alt={item.name} style={{
                    width:60, height:60, borderRadius:10, objectFit:'cover', flexShrink:0,
                  }}/>
                ) : (
                  <div style={{
                    width:60, height:60, borderRadius:10, background:'#f5f5f5',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                  }}>
                    {isGrocery
                      ? <ShoppingCart size={24} color='#ddd'/>
                      : <UtensilsCrossed size={24} color='#ddd'/>}
                  </div>
                )}

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#1a1a1a', marginBottom:2 }}>
                    {item.name}
                  </div>
                  {item.category && (
                    <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#ff6b35', fontWeight:600, marginBottom:3 }}>
                      <Tag size={10} color='#ff6b35'/> {item.category}
                    </div>
                  )}
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:800, fontSize:15, color:'#ff6b35' }}>₹{item.price}</span>
                    {isGrocery && item.unit && (
                      <span style={{ fontSize:11, color:'#aaa', background:'#f5f5f5', padding:'2px 7px', borderRadius:4 }}>
                        {item.unit}
                      </span>
                    )}
                    {isGrocery && item.stock !== undefined && (
                      <span style={{
                        fontSize:11, fontWeight:600, padding:'2px 7px', borderRadius:4,
                        background: item.stock > 0 ? '#f0fdf4' : '#fef2f2',
                        color:      item.stock > 0 ? '#16a34a' : '#ef4444',
                      }}>
                        {item.stock > 0 ? `Stock: ${item.stock}` : 'Out of stock'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Toggle button */}
                <div style={{ flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                  <button
                    onClick={() => toggleAvailability(item)}
                    disabled={isToggling}
                    style={{ background:'none', border:'none', cursor: isToggling ? 'not-allowed':'pointer', padding:0 }}
                  >
                    {isToggling ? (
                      <Loader2 size={28} color='#ff6b35' style={{ animation:'spin 0.6s linear infinite' }}/>
                    ) : isAvail ? (
                      <ToggleRight size={36} color='#10b981'/>
                    ) : (
                      <ToggleLeft size={36} color='#d1d5db'/>
                    )}
                  </button>
                  <span style={{
                    fontSize:10, fontWeight:700,
                    color: isAvail ? '#10b981' : '#ef4444',
                  }}>
                    {isAvail ? 'Available' : 'Unavailable'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const S = {
  page:        { padding:'16px', paddingBottom:80, background:'#f8f8f8', minHeight:'100vh' },
  header:      { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14, flexWrap:'wrap', gap:10 },
  title:       { fontSize:22, fontWeight:800, color:'#1a1a1a' },
  sub:         { fontSize:13, color:'#888', marginTop:3 },
  refreshBtn:  { padding:8, borderRadius:12, background:'#fff', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' },
  search:      { display:'flex', alignItems:'center', gap:10, background:'#fff', borderRadius:14, padding:'12px 16px', marginBottom:12, boxShadow:'0 2px 8px rgba(0,0,0,0.05)' },
  searchInput: { flex:1, fontSize:14, color:'#1a1a1a', background:'none', border:'none', outline:'none' },
  cats:        { display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' },
  cat:         { padding:'6px 14px', borderRadius:20, background:'#f0f0f0', color:'#666', fontSize:12, fontWeight:500, border:'none', cursor:'pointer' },
  catActive:   { background:'#ff6b35', color:'#fff' },
  empty:       { textAlign:'center', padding:'60px 20px' },
};